import React, { useState } from 'react';
import './ContactUs.css';
import { submitForm } from '../utils/formSubmit';
import Swal from 'sweetalert2';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitForm('Contact Us Form', formData);
      Swal.fire({
        title: 'Message Sent!',
        text: `Thank you, ${formData.name}! We will get back to you shortly.`,
        icon: 'success',
        confirmButtonColor: '#00ca65'
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: 'Failed to send message. Please try again later.',
        icon: 'error',
        confirmButtonColor: '#00ca65'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Sub Hero Banner */}
      <section
        className="contact-hero"
        style={{
          backgroundImage: `url('${process.env.PUBLIC_URL}/assets/images/home-banner.jpg')`
        }}
      >
        <h1 className="contact-hero__title">Contact Us</h1>
        <nav className="contact-hero__breadcrumb" aria-label="breadcrumb">
          <a href="/" className="contact-hero__breadcrumb-link">
            Home
          </a>
          <span className="contact-hero__breadcrumb-separator">&gt;</span>
          <span className="contact-hero__breadcrumb-current">Contact Us</span>
        </nav>
      </section>

      {/* Main Content Section */}
      <section className="contact-section">
        <div className="contact-grid">
          {/* Left Column: Map iframe instead of cards */}
          <div className="contact-map-col">
            <h2 className="contact-info-col__title">Find Us</h2>
            <p className="contact-info-col__desc" style={{ marginBottom: '20px' }}>
              We provide online Quran classes globally. Contact us from anywhere around the world.
            </p>
            <div className="contact-map-wrapper">
              <iframe
                title="Location Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d186100.6724436871!2d-78.8488!3d42.8864!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89d3126152dfe5a1%3A0x582437df3659e1f!2sBuffalo%2C%20NY!5e0!3m2!1sen!2sus!4v1689000000000!5m2!1sen!2sus"
                width="100%"
                height="450"
                style={{ border: 0, borderRadius: '12px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="contact-form-card">
            <h2 className="contact-form-card__title">Send Us a Message</h2>
            <p className="contact-form-card__desc">
              Please fill out the form below and we will get in touch with you as soon as possible.
            </p>

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="contact-form__row">
                <div className="contact-form__group">
                  <label htmlFor="name" className="contact-form__label">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="contact-form__input"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div className="contact-form__group">
                  <label htmlFor="email" className="contact-form__label">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="contact-form__input"
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>

              <div className="contact-form__row">
                <div className="contact-form__group">
                  <label htmlFor="phone" className="contact-form__label">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="contact-form__input"
                    placeholder="Enter your phone number"
                    required
                  />
                </div>

                <div className="contact-form__group">
                  <label htmlFor="subject" className="contact-form__label">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="contact-form__input"
                    placeholder="Subject of your message"
                    required
                  />
                </div>
              </div>

              <div className="contact-form__group">
                <label htmlFor="message" className="contact-form__label">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="contact-form__textarea"
                  placeholder="How can we help you?"
                  required
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="contact-form__submit"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>

        {/* Contact Cards rendered below the main section */}
        <div className="contact-cards-bottom-wrapper">
          <h2 className="contact-info-col__title contact-section__title--centered">Contact Details</h2>
          
          <div className="contact-cards-bottom-grid">
            {/* Card: Phone & WhatsApp */}
            <div className="contact-card">
              <div className="contact-card__icon-wrapper">
                <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6.62 10.79a15.15 15.15 0 006.59 6.59l2.2-2.2a1 1 0 011.11-.27c1.12.37 2.33.57 3.57.57a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.24.2 2.45.57 3.57a1 1 0 01-.27 1.11l-2.2 2.2z" />
                </svg>
              </div>
              <div className="contact-card__details">
                <h3 className="contact-card__title">Phone & WhatsApp</h3>
                <p className="contact-card__text">
                  USA: <a href="tel:+17164577657" className="contact-card__link">+1 716 457 7657</a>
                </p>
                <p className="contact-card__text">
                  Canada: <a href="tel:+16477385707" className="contact-card__link">+1 647 738 5707</a>
                </p>
                <p className="contact-card__text">
                  UK: <a href="tel:+447441394281" className="contact-card__link">+44 7441 394281</a>
                </p>
                <p className="contact-card__text">
                  WhatsApp: <a href="https://wa.me/17164577657" target="_blank" rel="noopener noreferrer" className="contact-card__link">+1 716-457-7657</a>
                </p>
              </div>
            </div>

            {/* Card: Email Support */}
            <div className="contact-card">
              <div className="contact-card__icon-wrapper">
                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="contact-card__details">
                <h3 className="contact-card__title">Email Support</h3>
                <p className="contact-card__text">
                  For enrollment, support, or general queries:
                </p>
                <p className="contact-card__text" style={{ marginTop: '10px' }}>
                  <a href="mailto:info@alquranlesson.com" className="contact-card__link">info@alquranlesson.com</a>
                </p>
              </div>
            </div>

            {/* Card: Availability */}
            <div className="contact-card">
              <div className="contact-card__icon-wrapper">
                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="contact-card__details">
                <h3 className="contact-card__title">Our Availability</h3>
                <p className="contact-card__text">
                  We are open and available 24 hours a day, 7 days a week, online.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
